import Column from '@components/Column';
import Header from '@components/Header';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import PinContents from '../common/PinContents';
import PinKeyboard from '../common/PinKeyboard';
import { PinContext, PinContextProvider } from './contexts/PinContext';
import useRegisterPin from './hooks/useRegisterPin';
import useResetPin from './hooks/useResetPin';
import useSetBioDevice from './hooks/useSetBioDevice';

declare module '@stackflow/config' {
    interface Register {
        EasyCardPinActivity: {
            /** 없으면 PIN 등록(두 번 입력), bioDevice면 PIN 확인 후 생체인증 기기 등록(한 번 입력) */
            mode?: 'bioDevice';
            /** 본인인증으로 받은 토큰. 있으면 PIN 재설정(두 번 입력) */
            pinResetToken?: string;
        }
    }
}

const EasyCardPinActivity: ActivityComponentType<'EasyCardPinActivity'> = ({ params }) => {
    const { t } = useTranslation('easyCard');
    const { pinResetToken } = params;
    const isBioDevice = params.mode === 'bioDevice';
    const { handleRegisterPin } = useRegisterPin();
    const { attempt: bioDeviceAttempt, handleSetBioDevice } = useSetBioDevice();
    const { attempt: resetAttempt, handleResetPin } = useResetPin(pinResetToken);
    const getHandler = () => {
        if (isBioDevice) {
            return handleSetBioDevice;
        }
        return pinResetToken ? handleResetPin : handleRegisterPin;
    };
    const getTitle = () => {
        if (isBioDevice) {
            return t('pin.bioDevice.title');
        }
        return t(pinResetToken ? 'pin.reset.title' : 'pin.title');
    };
    return (
        <AppScreen>
            {/* 생체인증 등록·PIN 재설정에 실패하면 입력을 처음부터 다시 받는다 */}
            <PinContextProvider key={`${bioDeviceAttempt}-${resetAttempt}`}
                confirm={!isBioDevice}
                onPinChanged={getHandler()}>
                <Column fullWidth fullHeight >
                    <Header title={getTitle()} />
                    <PinForm showUseBio={!isBioDevice}/>
                </Column>
            </PinContextProvider>
        </AppScreen>
    );
};

function PinForm({ showUseBio }: { showUseBio: boolean }) {
    const { t } = useTranslation('easyCard');
    const {
        pin, setPin, useBio, setUseBio, isCheck, isMismatch,
    } = useContext(PinContext);
    const getGuide = () => {
        if (isMismatch) {
            return t('pin.mismatch');
        }
        return t(isCheck ? 'pin.confirmGuide' : 'pin.inputGuide');
    };
    return (
        <>
            <PinContents pin={pin}
                guide={getGuide()}
                isError={isMismatch}
                useBio={useBio}
                onUseBioChange={showUseBio ? setUseBio : undefined}
                alwaysShowUseBio/>
            <PinKeyboard pin={pin}
                onPinChange={setPin}
                shuffleKey={isCheck}/>
        </>
    );
}

export default EasyCardPinActivity;
